<script setup>
import { computed } from "vue";

import FormField from "./FormField.vue";
import SvgIcon from "./SvgIcon.vue";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  options: { type: Array, default: () => [] },
  modelValue: { type: [String, Array, Number], default: "" },
  multiple: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  placeholder: { type: String, default: "" },
  id: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  labelProp: { type: String, default: "label" },
  valueProp: { type: String, default: "value" },
  label: { type: String, default: "" },
  error: { type: String, default: "" },
  info: { type: String, default: "" },
});
const emit = defineEmits(["update:modelValue", "change"]);
const selectOptions = computed(() =>
  props.options.map((option) => {
    if (option && (option[props.labelProp] || option[props.valueProp])) {
      return option;
    }
    return { [props.labelProp]: option, [props.valueProp]: option };
  }),
);

const selectedValue = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    emit("update:modelValue", newValue);
    emit("change", newValue);
  },
});
</script>

<template>
  <FormField :id :label :error :info>
    <template v-if="$slots.label" #label>
      <slot name="label" />
    </template>
    <template v-if="$slots['label-info']" #label-info="{ id: descriptionId }">
      <slot :id="descriptionId" name="label-info" />
    </template>
    <template #default="{ describedBy }">
      <div class="select-wrap" :class="{ loading: loading }">
        <select
          v-bind="$attrs"
          :id
          v-model="selectedValue"
          class="reset"
          :multiple
          :disabled="disabled || loading"
          :aria-describedby="describedBy"
        >
          <template v-if="placeholder">
            <option v-if="selectedValue === ''" value="" disabled hidden>
              {{ placeholder }}
            </option>
          </template>

          <slot>
            <option
              v-for="option in selectOptions"
              :key="option.id || option[valueProp] || option[labelProp]"
              :value="option[valueProp]"
              :disabled="option.disabled"
            >
              {{ option[labelProp] }}
            </option>
          </slot>
        </select>
        <SvgIcon
          v-if="loading"
          class="loading-spinner"
          name="system/loader"
          width="18"
          height="18"
        />
      </div>
    </template>
  </FormField>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;

.select-wrap {
  position: relative;
  align-items: center;
  display: grid;
  grid-template-areas: "select";

  select {
    height: 36px;
    padding: 0 2rem 0 0.75rem;
    font-family: inherit;
    font-size: 0.8125rem;
    color: c.$text;
    background-color: c.$background-ui;
    border: 1px solid c.$border;
    border-radius: 6px;
    outline: none;
    cursor: pointer;
    appearance: none;

    &:hover:not(:focus) {
      border-color: c.$text-subtle;
    }

    &:focus {
      border-color: c.$main;
    }

    &:disabled {
      background-color: c.$background-grey-light;
      color: c.$text-disabled;
      cursor: not-allowed;
    }
  }
}

.select-wrap::after {
  content: "▾" / "";
  color: c.$text-subtle;
  justify-self: end;
  padding-right: 0.5rem;
  pointer-events: none;
  font-size: 0.875rem;
}

.loading.select-wrap {
  select {
    color: c.$text-subtle;
    background-image: linear-gradient(
      90deg,
      c.$background-grey-light 0%,
      rgb(255 255 255 / 60%) 50%,
      c.$background-grey-light 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.4s linear infinite;
  }

  &::after {
    display: none;
  }
}

.loading-spinner {
  justify-self: end;
  margin-right: 0.5rem;
  color: c.$main;
  pointer-events: none;
  grid-area: select;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

select,
.select-wrap::after {
  grid-area: select;
}
</style>
